import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { IoCloseSharp } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CreateModal from "../../../component/Model/CreateModel";
import { useAuthUserMutation } from "../../../store/api/authSlice";
import {
  capitalizeString,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import "./parent.scss";

const initialState = {
  email: "",
  role: "",
};

const AddParent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const displayName =
    slug === "parents"
      ? "Parent"
      : slug === "drivers"
        ? "Driver"
        : capitalizeString(slug);
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialState);
  const [saveType, setSaveType] = useState("");

  // Create New Parent query
  const [authUser, { isLoading }] = useAuthUserMutation();

  const handleClose = () => {
    saveType === "save" ? navigate(`/${slug}`) : setFormData({ email: "" });
    setShow(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t(`errors.${name}`)} ${t("errors.required")}`,
    }));
  };

  const handleChangeCheckbox = (value) => {
    setFormData((prev) => ({ ...prev, is_private: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    const { email } = formData;

    // Email validation
    if (!email || email.trim() === "") {
      newErrors.email = `${t("errors.email")} ${t("errors.email")} ${t("errors.required")}`;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("errors.invalid_email");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, key) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    setSaveType(key);
    try {
      const response = await authUser({
        user: formData,
        endPoint: "user/register",
        invalidate: slug,
      }).unwrap();
      if (response.statusCode === 201) {
        setShow(true);
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  useEffect(() => {
    if (slug) {
      const role = slug === "parents" ? "parent" : "driver";
      setFormData((prev) => ({ ...prev, role }));
    }
  }, [slug]);

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        {/* <div className="dbSubHd mb-4">{t(pageTitle.toLowerCase())}</div> */}

        <div className="card border-0">
          <div className="card-body p-4">
            <form onSubmit={(e) => handleSubmit(e, "save")}>
              <div className="row">
                <div className="col-lg-8">
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      {`${t("email")} ${t("address")}`}{" "}
                      <span className="text-danger">*</span>
                    </label>

                    <input
                      type="email"
                      className={`formInput ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      value={formData.email}
                      onChange={handleChange}
                      name="email"
                      placeholder={`${t("enter")} ${t("email")} ${t(
                        "address",
                      )}`}
                    />
                    {errors?.email && (
                      <span className="text-danger small">{errors.email}</span>
                    )}
                    <div className="form-text text-muted mt-1">
                      {t("invitation-email")}
                    </div>
                  </div>

                  {slug === "parents" && (
                    <div className="mb-4">
                      <Form.Check
                        type="checkbox"
                        id="charity"
                        className="mb-0"
                        checked={formData?.is_private || false}
                        onChange={(e) => handleChangeCheckbox(e.target.checked)}
                        label={
                          <span className="fw-medium">{t("fee-charged")}</span>
                        }
                      />
                      <div className="form-text text-muted mt-1">
                        {t("fee-waiver")}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-lg-4">
                  <div className="d-flex flex-column gap-3">
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        {/* <h6 className="card-title mb-3">Actions</h6> */}
                        <div className="d-grid gap-2">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={saveType === "save" && isLoading}
                            // size="md"
                            // className="py-3 fw-semibold"
                          >
                            {saveType === "save" && isLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </span>
                              </>
                            ) : (
                              <>
                                {t("save")}{" "}
                                {capitalizeString(slug?.slice(0, -1) || "")}
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline-dark"
                            disabled={
                              saveType === "saveAndAddOther" && isLoading
                            }
                            onClick={(e) => handleSubmit(e, "saveAndAddOther")}
                            // size="md"
                            // className="fw-semibold"
                          >
                            {saveType === "saveAndAddOther" && isLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    {t("loading")}...
                                  </span>
                                </span>
                              </>
                            ) : (
                              <>
                                {t("save")} {t("and")} {t("add")} {t("another")}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <h6 className="card-title mb-2">{t("information")}</h6>
                        <ul className="list-unstyled mb-0 small">
                          <li className="mb-1">
                            <i className="bi bi-info-circle me-2"></i>
                            {t("user-receive")}
                          </li>
                          <li className="mb-1">
                            <i className="bi bi-info-circle me-2"></i>
                            {t("complete-registration")}
                          </li>
                          <li>
                            <i className="bi bi-info-circle me-2"></i>
                            {t("account-activated")}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Modal show={show} centered>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          onClose={handleClose}
          title={`${displayName} Added Successfully!`}
          text={`${displayName} has been added.`}
        />
      </Modal>

      <style jsx>
        {`
          /* Information List */
          .list-unstyled li {
            padding: 4px 0;
            color: #6c757d;
            font-size: 14px;
          }

          .list-unstyled i {
            color: #6c757d;
            font-size: 14px;
          }

          /* Modal Styling */
          .modal-header {
            background-color: #f8f9fa;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          }

          .modal-title {
            color: #333;
            font-weight: 600;
          }
        `}
      </style>
    </>
  );
};

export default AddParent;
